/**
 * Unified analytics bridge for Happy Solutions.
 *
 * Fans every event out to:
 *   - GTM `dataLayer`         (`window.dataLayer.push`)
 *   - Google Analytics 4      (`window.gtag('event', ...)`)
 *   - Facebook / Meta Pixel   (`window.fbq('track' | 'trackCustom', ...)`)
 *   - Contentsquare           (`window._uxa.push(['trackEvent' | 'trackPageview', ...])`)
 *
 * It is safe to call before the underlying SDKs have finished loading – every
 * call is wrapped in try/catch and missing globals are silently ignored.
 *
 * Public API:
 *   trackEvent(name, params)
 *   trackPageView(path, title)
 *   initAutoTracking()  // call once at app boot
 */

const STANDARD_FB_EVENTS = new Set([
    'PageView', 'ViewContent', 'Search', 'AddToCart', 'AddToWishlist',
    'InitiateCheckout', 'AddPaymentInfo', 'Purchase', 'Lead', 'CompleteRegistration',
    'Contact', 'CustomizeProduct', 'Donate', 'FindLocation', 'Schedule',
    'StartTrial', 'SubmitApplication', 'Subscribe',
]);

const safe = (fn) => {
    try { fn(); } catch (e) { /* analytics must never break the app */ }
};

/**
 * Push to GTM dataLayer.
 */
export const dlPush = (payload) => {
    safe(() => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
    });
};

/**
 * Send a custom event everywhere.
 *
 * @param {string} name   Event name (snake_case recommended, e.g. `cta_click`).
 * @param {Object} params Optional event params.
 */
export const trackEvent = (name, params = {}) => {
    if (!name) return;
    const payload = { event: name, ...params };

    // GTM
    dlPush(payload);

    // GA4
    safe(() => {
        if (typeof window.gtag === 'function') {
            window.gtag('event', name, params);
        }
    });

    // Meta Pixel — standard events go through `track`, custom through `trackCustom`
    safe(() => {
        if (typeof window.fbq === 'function') {
            if (STANDARD_FB_EVENTS.has(name)) {
                window.fbq('track', name, params);
            } else {
                window.fbq('trackCustom', name, params);
            }
        }
    });

    // Contentsquare
    safe(() => {
        window._uxa = window._uxa || [];
        window._uxa.push(['trackDynamicVariable', { key: name, value: JSON.stringify(params).slice(0, 256) }]);
    });
};

/**
 * Fire a virtual page view across every analytics system.
 */
export const trackPageView = (path, title) => {
    const url = path || (typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/');
    const pageTitle = title || (typeof document !== 'undefined' ? document.title : '');

    // GTM
    dlPush({
        event: 'virtualPageview',
        page_path: url,
        page_title: pageTitle,
        page_location: typeof window !== 'undefined' ? window.location.href : '',
    });

    // GA4 — manual page_view (auto disabled in index.html so SPA navigation is counted)
    safe(() => {
        if (typeof window.gtag === 'function') {
            window.gtag('event', 'page_view', {
                page_title: pageTitle,
                page_location: typeof window !== 'undefined' ? window.location.href : url,
                page_path: url,
            });
        }
    });

    // Meta Pixel
    safe(() => {
        if (typeof window.fbq === 'function') {
            window.fbq('track', 'PageView');
        }
    });

    // Contentsquare — virtual page view
    safe(() => {
        window._uxa = window._uxa || [];
        window._uxa.push(['trackPageview', url]);
    });
};

/* ----------------------------------------------------------------------- */
/* Auto-tracking listeners (registered once)                                */
/* ----------------------------------------------------------------------- */

let autoInited = false;

const initScrollDepthTracking = () => {
    const fired = new Set();
    const milestones = [25, 50, 75, 100];

    const onScroll = () => {
        const doc = document.documentElement;
        const scrollTop = window.scrollY || doc.scrollTop || 0;
        const viewport = window.innerHeight || doc.clientHeight || 0;
        const full = (doc.scrollHeight || document.body.scrollHeight || 0) - viewport;
        if (full <= 0) return;
        const pct = Math.min(100, Math.round((scrollTop / full) * 100));
        milestones.forEach((m) => {
            if (pct >= m && !fired.has(m)) {
                fired.add(m);
                trackEvent('scroll_depth', { percent: m, page_path: window.location.pathname });
            }
        });
    };

    let ticking = false;
    window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
            onScroll();
            ticking = false;
        });
    }, { passive: true });

    // Reset milestones on SPA route change
    window.addEventListener('hashchange', () => fired.clear());
    window.addEventListener('popstate', () => fired.clear());
};

const initTimeOnPageTracking = () => {
    const stamps = [15, 30, 60, 180]; // seconds
    stamps.forEach((sec) => {
        setTimeout(() => {
            trackEvent('time_on_page', { seconds: sec, page_path: window.location.pathname });
        }, sec * 1000);
    });
};

const initClickTracking = () => {
    document.addEventListener('click', (e) => {
        const target = e.target.closest('a, button, [data-track]');
        if (!target) return;

        // Outbound link tracking
        if (target.tagName === 'A' && target.href) {
            try {
                const url = new URL(target.href, window.location.href);
                if (url.host && url.host !== window.location.host && !url.protocol.startsWith('javascript')) {
                    trackEvent('outbound_click', {
                        link_url: target.href,
                        link_domain: url.host,
                        link_text: (target.innerText || '').trim().slice(0, 120),
                    });
                    return;
                }

                // tel: / mailto: links
                if (url.protocol === 'tel:') {
                    trackEvent('click_tel', { value: url.href });
                    return;
                }
                if (url.protocol === 'mailto:') {
                    trackEvent('click_email', { value: url.href });
                    return;
                }
            } catch (e) { /* noop */ }
        }

        // data-track="event_name" opt-in attribute
        const explicit = target.getAttribute && target.getAttribute('data-track');
        if (explicit) {
            const extra = {};
            Array.from(target.attributes || []).forEach((a) => {
                if (a.name.startsWith('data-track-') && a.name !== 'data-track') {
                    extra[a.name.replace('data-track-', '')] = a.value;
                }
            });
            trackEvent(explicit, {
                ...extra,
                label: (target.innerText || target.getAttribute('aria-label') || '').trim().slice(0, 120),
            });
            return;
        }

        // CTA buttons / .edu-btn — fire a generic cta_click
        if (
            (target.classList && (target.classList.contains('edu-btn') || target.classList.contains('header-demo-cta') || target.classList.contains('premium-mobile-menu__cta')))
        ) {
            trackEvent('cta_click', {
                label: (target.innerText || target.getAttribute('aria-label') || '').trim().slice(0, 120),
                link_url: target.href || target.getAttribute('data-href') || '',
                page_path: window.location.pathname,
            });
        }
    }, true);
};

const initFormTracking = () => {
    document.addEventListener('submit', (e) => {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;
        trackEvent('form_submit', {
            form_id: form.id || '',
            form_name: form.getAttribute('name') || '',
            form_action: form.action || window.location.href,
            field_count: form.elements ? form.elements.length : 0,
        });
        // Treat any form submission as a Lead for Meta Pixel — useful for contact / quote forms
        safe(() => {
            if (typeof window.fbq === 'function') window.fbq('track', 'Lead');
        });
    }, true);

    // First interaction on any input → form_start (fires once per form per session)
    const started = new WeakSet();
    document.addEventListener('focusin', (e) => {
        const field = e.target;
        if (!field || !field.form || started.has(field.form)) return;
        started.add(field.form);
        trackEvent('form_start', {
            form_id: field.form.id || '',
            form_name: field.form.getAttribute('name') || '',
        });
    }, true);
};

const initVisibilityTracking = () => {
    const start = Date.now();
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            trackEvent('page_visibility_hidden', {
                duration_ms: Date.now() - start,
                page_path: window.location.pathname,
            });
        } else if (document.visibilityState === 'visible') {
            trackEvent('page_visibility_visible', { page_path: window.location.pathname });
        }
    });
};

/**
 * Wire up all the document-level listeners. Call once at app boot.
 */
export const initAutoTracking = () => {
    if (autoInited || typeof window === 'undefined' || typeof document === 'undefined') return;
    autoInited = true;
    safe(initScrollDepthTracking);
    safe(initTimeOnPageTracking);
    safe(initClickTracking);
    safe(initFormTracking);
    safe(initVisibilityTracking);

    // Expose helpers globally so non-React code (vanilla CTAs, third-party widgets)
    // can also push events without importing the module.
    window.HappyAnalytics = { trackEvent, trackPageView, dlPush };
};

export default {
    trackEvent,
    trackPageView,
    dlPush,
    initAutoTracking,
};

